import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { api, AVATAR_IDS, avatarSrc, Icon, Sheet } from "@tc/ui";
/**
 * The avatar gallery: 100 characters. Inside a company each one belongs to one person, so the taken ones are greyed
 * out with the holder's first name. Tap one to take it. Used for yourself, or by a manager for someone in their team.
 */
export function AvatarPicker({ open, onClose, onPicked, person }) {
    const [mine, setMine] = useState(null);
    const [taken, setTaken] = useState(new Map());
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!open)
            return;
        setError(null);
        void api(`/api/company/avatars${person ? `?for=${person.id}` : ""}`)
            .then((r) => { setMine(r.mine); setTaken(new Map(r.taken.map((t) => [t.id, t.by]))); }, (e) => setError(e.message));
    }, [open, person]);
    async function pick(id) {
        setBusy(id ?? "none");
        setError(null);
        try {
            if (person)
                await api(`/api/company/people/${person.id}/avatar`, { method: "PUT", body: { id } });
            else
                await api("/api/me/avatar-preset", { method: "PUT", body: { id } });
            setMine(id);
            onPicked(id ? `preset:${id}` : null);
            window.setTimeout(onClose, 380);
        }
        catch (e) {
            setError(e.message);
        }
        setBusy(null);
    }
    const free = AVATAR_IDS.filter((id) => !taken.has(id) && id !== mine);
    const surprise = () => { const id = free[Math.floor(Math.random() * free.length)]; if (id)
        void pick(id); };
    return (_jsxs(Sheet, { open: open, onClose: onClose, wide: true, title: person ? `Avatar for ${person.name.split(/\s+/)[0]}` : "Choose your avatar", foot: _jsxs("div", { className: "tc-avp-foot", children: [_jsxs("button", { className: "tc-btn", onClick: surprise, disabled: !free.length || !!busy, children: [_jsx(Icon, { name: "sparkle", size: 16 }), " Surprise me"] }), mine && _jsx("button", { className: "tc-link", onClick: () => void pick(null), disabled: !!busy, children: person ? "Remove avatar" : "Remove (use my photo or initials)" })] }), children: [_jsxs("p", { className: "tc-avp-sub", children: ["Everyone in your company gets a different one. ", taken.size ? `${taken.size} taken, ${100 - taken.size} to choose from.` : "All 100 are free."] }), error && _jsx("div", { className: "tc-error", role: "alert", children: error }), _jsx("div", { className: "tc-avp-grid", role: "listbox", "aria-label": "Avatars", children: AVATAR_IDS.map((id, i) => {
                    const by = taken.get(id);
                    const on = mine === id;
                    return (_jsxs("button", { role: "option", "aria-selected": on, "aria-label": by ? `Taken by ${by}` : on ? "Your avatar" : "Avatar", disabled: !!by || !!busy, className: `tc-avp${on ? " on" : ""}${by ? " taken" : ""}${busy === id ? " busy" : ""}`, style: { ["--i"]: i % 20 }, onClick: () => void pick(id), children: [_jsx("img", { src: avatarSrc(`preset:${id}`), alt: "", loading: "lazy" }), by && _jsx("small", { children: by }), on && _jsx("span", { className: "tc-avp-tick", children: _jsx(Icon, { name: "check", size: 13 }) })] }, id));
                }) })] }));
}
